import type { RoomOccupancy, RoomsParticipantDistribution } from '@/domain/shared/types';
import { createRoomsParticipantDistributionMapper } from '@/scopes/search/search/infrastructure/api/filtersSelectionMapper/mappers/roomsParticipantDistributionMapper';
import type {
  Parser,
  QueryStringParameters,
} from '../../scopes/common/infrastructure/query-strings/queryStringSerializer';
import { isEmpty } from 'ramda';
import { sub, format } from 'date-fns';
import { CHILDREN_MIN_AGE, CHILDREN_MAX_AGE } from '@/domain/shared/constants';

export interface Filters {
  DepartureAirport?: string[];
  Duration?: string[];
  Mealplan?: string;
  Month?: string;
  TransportType?: string;
  Participants?: RoomOccupancy[];
}

interface Dependencies {
  roomsParticipantDistributionMapper: ReturnType<typeof createRoomsParticipantDistributionMapper>;
}

const defaultParticipants: RoomOccupancy[] = [
  {
    adults: 2,
    children: [],
    babies: [],
  },
];

export const createPricesFiltersParser = (dependencies: Partial<Dependencies> = {}): Parser<Filters> => {
  const { roomsParticipantDistributionMapper = createRoomsParticipantDistributionMapper() } = dependencies;

  function serialize({ Participants, ...rest }: Filters) {
    return {
      ...rest,
      Participants:
        Participants && !isEmpty(Participants)
          ? roomsParticipantDistributionMapper(Participants ?? [])
          : roomsParticipantDistributionMapper(defaultParticipants),
    };
  }

  function parse(input: QueryStringParameters) {
    const derivedMonth = getMonth(input.Month as string, input.DepartureDate as string[]);
    return {
      DepartureAirport: (input.DepartureAirport as string[]) ?? [],
      Duration: (input.Duration as string[]) ?? [],
      RoomType: (input.RoomType as string[]) ?? [],
      Participants: input.Participants ? mapParticipants(input.Participants as string[][]) : defaultParticipants,
      ...(derivedMonth && { Month: derivedMonth }),
      ...(input.Mealplan ? { Mealplan: getFilterValue(input.Mealplan as string | string[]) } : {}),
      ...(input.TransportType ? { TransportType: getFilterValue(input.TransportType as string | string[]) } : {}),
      ...((input.DepartureDate as string[]) && { DepartureDate: input.DepartureDate as string[] }),
    };
  }

  function mapParticipants(rooms: string[][]) {
    const adultAgeThreshold = sub(new Date(), { years: CHILDREN_MAX_AGE, days: 1 });
    const childrenAgeThreshold = sub(new Date(), { years: CHILDREN_MIN_AGE, days: 1 });

    const mappedParticipants: RoomsParticipantDistribution = [];

    rooms.forEach((participants) => {
      const result: RoomOccupancy = {
        adults: 0,
        children: [],
        babies: [],
      };

      participants.forEach((participantBirthDate) => {
        const birthDate = new Date(participantBirthDate);

        if (birthDate < adultAgeThreshold) {
          result.adults += 1;
        } else if (birthDate < childrenAgeThreshold) {
          result.children.push(birthDate);
        } else {
          result.babies.push(birthDate);
        }
      });

      mappedParticipants.push(result);
    });

    return mappedParticipants;
  }

  function getMonth(month?: string, date?: string[]) {
    if (month) {
      return month;
    }
    if (date && date.length > 0) {
      return format(new Date(date[0]), 'yyyy-MM');
    }
    return null;
  }

  function getFilterValue(value: string | string[]): string {
    return value && Array.isArray(value) ? value.join() : (value as string);
  }
  return { serialize, parse };
};
